import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Seo } from "../../components";
import { CaptchaWidget } from "../../components/CaptchaWidget/CaptchaWidget";
import "./HomePage.scss";
import { CONTACT_URL } from "../../features/UrlHelper";
import { handleApiError } from "../../features/errorHandler";
import { formatDateSk } from "../../features/dateFormaters";

// Obsah a rozloženie podľa návrhu triferis-landing (brand manuál v1.3).
const IMG = "/pictures/triferis";

const FACTS = [
  { value: "116", label: "lôžok v hlavnej budove a chatkách" },
  { value: "18 + 5", label: "izieb s vlastnou kúpeľňou a chatiek pre 6 – 8" },
  { value: "15 min", label: "autobusom na Chopok juh, Tále 3,5 km" },
  { value: "1", label: "reštaurácia s 80 miestami" },
  { value: "2", label: "spoločenské miestnosti s kapacitou 48 osôb" },
  { value: "2", label: "chillout zóny" },
];

const SEGMENTS = [
  {
    image: `${IMG}/lyziar-chopok.jpg`,
    imageAlt: "Lyžiar na Chopku",
    who: "Sokol · školy a výcviky",
    title: "Lyžiarske výcviky a školy v prírode",
    text: "Plná penzia, obed na svahu, doprava na Chopok, pedagóg 1 : 10 zadarmo a zdravotník na požiadanie. Lyžiarske výcviky december – marec, školy v prírode apríl – jún a september – október.",
    price: "od 33 €",
    priceNote: "/ osoba / noc",
    link: "Dopyt pre školu →",
    hash: "kontakt",
  },
  {
    image: `${IMG}/terasa.jpg`,
    imageAlt: "Terasa reštaurácie",
    who: "Medveď · rodiny a priatelia",
    title: "Víkendy pre rodiny",
    text: "Izby pre 2 až 5 osôb alebo celá chatka pre 6 či 8. Ráno káva, čaj a pečivo zdarma, večer oheň, bazén a ihriská pre deti. Bystrianska jaskyňa priamo v obci.",
    price: "od 34 €",
    priceNote: "/ osoba / noc, dieťa 3 – 11 r. 21 €",
    link: "Pozrieť izby →",
    hash: "ubytovanie",
  },
  {
    image: `${IMG}/spolocenska-miestnost.jpg`,
    imageAlt: "Spoločenská miestnosť",
    who: "Rys · firmy a združenia",
    title: "Sústredenia, teambuildingy, stretnutia",
    text: "Dve spoločenské miestnosti pre 48 ľudí, stan pre 40, reštaurácia pre seba. Možnosť prenájmu celého areálu, catering, program s inštruktormi. Skupine 12 – 24 osôb prenajmeme prízemie, skupine 24 – 48 osôb poschodie.",
    price: "1 900 €",
    priceNote: "/ noc – celý areál, skupiny od 12 osôb",
    link: "Nezáväzná ponuka →",
    hash: "kontakt",
  },
];

const ROOMS = [
  {
    image: `${IMG}/izba.jpg`,
    imageAlt: "Izba v hlavnej budove",
    label: "Hlavná budova · 80 lôžok",
    title: "18 izieb pre 2 až 5 osôb",
    text: "Každá s vlastnou kúpeľňou a WC, televízorom a Wi-Fi. Svetlé drevo, výhľad do doliny.",
  },
  {
    image: `${IMG}/chatky.jpg`,
    imageAlt: "Drevené chatky",
    label: "Chatky · 36 lôžok",
    title: "5 chatiek pre 6 a 8 osôb",
    text: "Celoročne, aj samostatne bez hlavnej budovy. Ideálne pre rodinu s priateľmi alebo menší tím.",
  },
];

const ROOM_LIST = [
  { count: "3×", label: "dvojlôžková premium (+ 2 prístelky)" },
  { count: "7×", label: "štvorlôžková izba" },
  { count: "8×", label: "päťlôžková izba" },
  { count: "2×", label: "chatka pre 6 osôb" },
  { count: "3×", label: "chatka pre 8 osôb" },
];

// Platí pre izby aj chatky
const STAY_RULES =
  "Check-in 14:00 – 18:00 · check-out do 10:00 · minimálne 2 noci, cez Vianoce 3 · domáce zvieratá neprijímame";

const PRICES = [
  { value: "34 €", label: "dospelá osoba / noc" },
  { value: "21 €", label: "dieťa 3 – 11 rokov / noc" },
  { value: "zdarma", label: "dieťa do 3 rokov" },
  { value: "+ 8 €", label: "raňajky / osoba, od 9. 1. 2027" },
  { value: "1 900 €", label: "celý areál / noc" },
];

const PRICE_TERMS = [
  "Daň z ubytovania 1 € / osoba / noc je v cene.",
  "Izby účtujeme pri minimálnej obsadenosti.",
  "Storno bez poplatku do 7 dní pred príchodom.",
];

// Ikony služieb z návrhu: mriežka 40 × 40, ťah 1,8 px, bez výplne (štýl je v SCSS).
const AMENITIES = [
  {
    label: "Reštaurácia s terasou, 80 miest",
    icon: (
      <>
        <path d="M10 12 L10 30 M14 12 L14 30 M10 20 L14 20" />
        <path d="M26 12 C22 14 22 22 26 22 L26 30" />
        <path d="M8 34 L32 34" />
      </>
    ),
  },
  {
    label: "Kuchyňa pre hostí",
    icon: (
      <>
        <path d="M11 18 L29 18 L27 32 L13 32 Z" />
        <path d="M11 21 L7 21 M29 21 L33 21" />
        <path d="M12 14 L28 14 M20 10 L20 14" />
      </>
    ),
  },
  {
    label: "2 spoločenské miestnosti pre 48 ľudí",
    icon: (
      <>
        <path d="M12 10 L12 30 M28 10 L28 30" />
        <path d="M12 10 L28 10 L28 14 L12 14 Z" />
        <path d="M14 20 L26 20 M14 25 L26 25" />
      </>
    ),
  },
  {
    label: "Stan pre 40 osôb",
    icon: (
      <>
        <path d="M8 32 L8 18 L20 8 L32 18 L32 32 Z" />
        <path d="M16 32 L16 22 L24 22 L24 32" />
      </>
    ),
  },
  {
    label: "Sezónny vonkajší bazén (jún – september)",
    icon: (
      <>
        <path d="M8 26 C12 22 16 30 20 26 C24 22 28 30 32 26" />
        <path d="M8 32 C12 28 16 36 20 32 C24 28 28 36 32 32" />
        <path d="M10 20 L30 20 L26 12 L14 12 Z" />
      </>
    ),
  },
  {
    label: "Futbalové ihrisko a plážový volejbal",
    icon: (
      <>
        <circle cx="20" cy="20" r="12" />
        <path d="M14 12 L18 20 L12 26" />
        <path d="M26 12 L22 20 L28 26" />
      </>
    ),
  },
  {
    label: "Ohnisko a lúka",
    icon: (
      <>
        <path d="M14 30 C10 24 14 18 18 14 C18 20 22 20 22 14 C26 18 30 24 26 30 Z" />
        <path d="M8 34 L32 34" />
      </>
    ),
  },
  {
    label: "Stolný tenis a chill-out zóna",
    icon: (
      <>
        <path d="M6 26 L34 26" />
        <path d="M8 26 L8 14 L20 14 L20 26" />
        <path d="M20 20 L32 20 L32 26" />
        <path d="M6 30 L6 26 M34 30 L34 26" />
      </>
    ),
  },
  {
    label: "Karaoke",
    icon: (
      <>
        <circle cx="20" cy="12" r="5" />
        <path d="M17 17 L18 28 L22 28 L23 17" />
        <path d="M20 28 L20 34 M15 34 L25 34" />
      </>
    ),
  },
  {
    label: "Šípky a stolný futbal",
    icon: (
      <>
        <circle cx="18" cy="22" r="11" />
        <circle cx="18" cy="22" r="5" />
        <path d="M18 22 L32 8 M27 8 L32 8 L32 13" />
      </>
    ),
  },
  {
    label: "Parkovisko a doprava na svah",
    icon: (
      <>
        <path d="M6 24 L10 16 L30 16 L34 24 Z" />
        <path d="M10 24 L10 30 M30 24 L30 30" />
        <circle cx="14" cy="30" r="2" />
        <circle cx="26" cy="30" r="2" />
      </>
    ),
  },
  {
    label: "Lyžiareň",
    icon: (
      <>
        <path d="M14 34 L14 11 C14 8 16 7 17 9" />
        <path d="M24 34 L24 11 C24 8 26 7 27 9" />
        <path d="M8 24 L32 24" />
      </>
    ),
  },
  {
    label: "Úschovňa bicyklov",
    icon: (
      <>
        <circle cx="11" cy="26" r="6" />
        <circle cx="29" cy="26" r="6" />
        <path d="M11 26 L16 16 L26 16 L19 26 Z" />
        <path d="M26 16 L29 26 M24 12 L27 12 L26 16 M14 15 L18 15" />
      </>
    ),
  },
  {
    label: "Wi-Fi v celom areáli",
    icon: (
      <>
        <path d="M8 26 C8 16 14 12 20 12 C26 12 32 16 32 26" />
        <path d="M12 26 C12 18 16 16 20 16 C24 16 28 18 28 26" />
        <circle cx="20" cy="20" r="1" />
      </>
    ),
  },
  {
    label: "Obed na svahu počas výcviku",
    icon: (
      <>
        <path d="M20 8 L20 22" />
        <path d="M12 12 L28 12" />
        <path d="M8 32 L16 20 L24 26 L32 22" />
      </>
    ),
  },
];

const AROUND = [
  {
    image: `${IMG}/bystrianska-jaskyna.jpg`,
    imageAlt: "Bystrianska jaskyňa",
    distance: "priamo v obci · 0,9 km",
    title: "Bystrianska jaskyňa",
    text: "Sprístupnená jaskyňa s liečivou klímou. Vhodná aj pre najmenšie deti a program na daždivý deň.",
  },
  {
    image: `${IMG}/dumbier.jpg`,
    imageAlt: "Ďumbier",
    distance: "celodenná túra",
    title: "Ďumbier, 2 043 m",
    text: "Najvyšší vrch Nízkych Tatier priamo z doliny. Lesné cesty Bystrianskou a Ďumbierskou dolinou sú ideálne na bicykel.",
  },
  {
    image: `${IMG}/lyziar-chopok.jpg`,
    imageAlt: "Chopok juh",
    distance: "15 min autobusom",
    title: "Chopok juh, Tále, Mýto",
    text: "Tri lyžiarske strediská do 15 minút: Mýto pod Ďumbierom 2,9 km, Tále 3,5 km, Chopok juh autobusom.",
  },
  {
    image: `${IMG}/hero-ohen.jpg`,
    imageAlt: "Deti v prírode",
    distance: "pre školy a rodiny",
    title: "Čiernohronská železnička, farma Ajax, golf Tále",
    text: "Historická lesná železnica, kontaktná farma so zvieratami a golfové ihrisko – tri výlety na tri rôzne povahy.",
  },
];

const STAY_TYPES = [
  "Lyžiarsky výcvik",
  "Škola v prírode",
  "Firemné sústredenie",
  "Skupinový pobyt",
  "Svadba / oslava",
  "Izby - samostatne"
];

const EMPTY_INQUIRY = {
  name: "",
  contact: "",
  stayType: STAY_TYPES[0],
  guests: "",
  arrival: "",
  departure: "",
  note: "",
};

const NOTE_DEFAULT =
  "Nezáväzný dopyt. Odpovieme s konkrétnou cenou a termínmi.";
const NOTE_SENT = "Ďakujeme, dopyt sme prijali. Ozveme sa do 24 hodín.";
const NOTE_CAPTCHA =
  "Počkajte, prosím, na overenie proti robotom a skúste to znova.";
const NOTE_ERROR =
  "Dopyt sa nepodarilo odoslať. Napíšte nám na info@triferis.sk alebo zavolajte na +421 944 526 240.";

const MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Bystr%C3%A1%20118%2C%20977%2001%20Bystr%C3%A1";
// Vložená mapa cez iframe – adresu si dohľadá Google, API kľúč netreba
const MAP_EMBED_URL =
  "https://maps.google.com/maps?q=Bystr%C3%A1%20118%2C%20977%2001%20Bystr%C3%A1&z=14&output=embed";

// Dopyt ide cez existujúci kontaktný endpoint – polia formulára sa poskladajú do textu správy.
function buildInquiryMessage(inquiry) {
  return [
    `Typ pobytu: ${inquiry.stayType}`,
    inquiry.guests && `Počet osôb: ${inquiry.guests}`,
    inquiry.arrival && `Príchod: ${formatDateSk(inquiry.arrival)}`,
    inquiry.departure && `Odchod: ${formatDateSk(inquiry.departure)}`,
    inquiry.note && `Poznámka: ${inquiry.note}`,
  ]
    .filter(Boolean)
    .join("\n");
}

function HomePage() {
  const location = useLocation();

  const [inquiry, setInquiry] = useState(EMPTY_INQUIRY);
  const [inquiryStatus, setInquiryStatus] = useState("idle"); // idle | sending | sent | error
  const [inquiryNote, setInquiryNote] = useState(NOTE_DEFAULT);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  // Kotvy z hlavičky a tlačidiel (/#skupiny, /#kontakt …)
  useEffect(() => {
    if (!location.hash) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView();
  }, [location.hash, location.key]);

  const handleInquiryChange = (field, value) => {
    setInquiry((prev) => ({ ...prev, [field]: value }));
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      setInquiryStatus("error");
      setInquiryNote(NOTE_CAPTCHA);
      return;
    }

    setInquiryStatus("sending");

    try {
      const { data } = await axios.post(CONTACT_URL, {
        name: inquiry.name,
        email: inquiry.contact,
        subject: `Dopyt – ${inquiry.stayType}`,
        message: buildInquiryMessage(inquiry),
        cfTurnstileToken: captchaToken,
      });

      if (data.success) {
        setInquiryStatus("sent");
        setInquiryNote(NOTE_SENT);
        setInquiry(EMPTY_INQUIRY);
      } else {
        setInquiryStatus("error");
        setInquiryNote(data.message || NOTE_ERROR);
      }
    } catch (error) {
      const detail = handleApiError(error, NOTE_ERROR);
      setInquiryStatus("error");
      // 4xx (napr. captcha) má vlastnú hlášku z backendu; pri výpadku ponúkneme kontakt
      setInquiryNote(error?.response?.status < 500 ? detail : NOTE_ERROR);
    } finally {
      // Turnstile token je jednorazový
      captchaRef.current?.reset();
      setCaptchaToken(null);
    }
  };

  return (
    <div className="home">
      <Seo
        description="Rozumieme rodičom, učiteľom i deťom. Preto v Triferis kladieme dôraz na bezpečnosť, kvalitné ubytovanie, dobrú stravu a v neposlednom rade zážitky. Ešte váhate, či sme tá správna voľba? Zavolajte nám: +421 944 526 240, alebo napíšte na info@triferis.sk"
        canonical="/"
      />

      {/* HERO */}
      <section className="home-hero">
        <img
          className="home-hero__img"
          src={`${IMG}/hero-ohen.jpg`}
          alt="Deti pri ohni v jesennom lese pri Bystrej"
        />
        <div className="home-hero__shade" />
        <div className="home__wrap home-hero__wrap">
          <div className="home__eyebrow home__eyebrow--on-dark home-hero__eyebrow">
            Bystrá · Nízke Tatry · Chopok juh
          </div>
          <h1 className="home-hero__title">
            Rodinný penzión,
            <br />
            stvorený pre detský úsmev.
          </h1>
          <p className="home-hero__sub">
            Rozumieme rodičom, učiteľom i deťom. Preto v Triferis kladieme dôraz
            na bezpečnosť, kvalitné ubytovanie, dobrú stravu a v neposlednom
            rade zážitky. Ešte váhate, či sme tá správna voľba? Zavolajte nám:{" "}
            <a className="home-hero__link" href="tel:+421 944 526 240">
              +421 944 526 240
            </a>
            , alebo napíšte na{" "}
            <a className="home-hero__link" href="mailto:info@triferis.sk">
              info@triferis.sk
            </a>
          </p>
          <div className="home-hero__ctas">
            <Link to="/#skupiny" className="home__btn home__btn--accent">
              Ponuka pre školy
            </Link>
            <Link to="/#ubytovanie" className="home__btn home__btn--light">
              Ponuka izieb
            </Link>
          </div>
        </div>
      </section>

      {/* FAKTY */}
      <div className="home-facts">
        <div className="home__wrap home-facts__grid">
          {FACTS.map((fact) => (
            <div key={fact.value} className="home-facts__item">
              <b className="home-facts__value">{fact.value}</b>
              <span className="home-facts__label">{fact.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* PRE SKUPINY */}
      <section id="skupiny" className="home__section">
        <div className="home__wrap">
          <div className="home__head">
            <div className="home__eyebrow">Pre koho tu sme</div>
            <h2 className="home__title">
              Špecializujeme sa na veľké skupiny, postaráme sa aj o
              individuálnych rekreantov.
            </h2>
            <p className="home__lead">
              Cez týždeň patrí areál skupinám – školám, firmám, združeniam. Cez
              víkend rodinám a partiám priateľov. Ceny sú za osobu a noc, bez
              skrytých položiek.
            </p>
          </div>

          <div className="home-segs">
            {SEGMENTS.map((seg) => (
              <div key={seg.who} className="home-seg">
                <div className="home-seg__photo">
                  <img src={seg.image} alt={seg.imageAlt} loading="lazy" />
                </div>
                <div className="home-seg__body">
                  <div className="home-seg__who">{seg.who}</div>
                  <h3 className="home-seg__title">{seg.title}</h3>
                  <p className="home-seg__text">{seg.text}</p>
                  <div className="home-seg__price">
                    <div className="home-seg__price-value">
                      <b>{seg.price}</b> <small>{seg.priceNote}</small>
                    </div>
                    <Link to={`/#${seg.hash}`} className="home-seg__link">
                      {seg.link}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UBYTOVANIE */}
      <section
        id="ubytovanie"
        className="home__section home__section--flush-top"
      >
        <div className="home__wrap">
          <div className="home__head">
            <div className="home__eyebrow">Ubytovanie</div>
            <h2 className="home__title">
              Izby v hlavnej budove a drevené chatky
            </h2>
          </div>

          <div className="home-rooms">
            {ROOMS.map((room) => (
              <div key={room.label} className="home-room">
                <img
                  className="home-room__img"
                  src={room.image}
                  alt={room.imageAlt}
                  loading="lazy"
                />
                <div className="home-room__shade" />
                <div className="home-room__text">
                  <div className="home-room__label">{room.label}</div>
                  <h3 className="home-room__title">{room.title}</h3>
                  <p className="home-room__desc">{room.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="home-room-list">
            {ROOM_LIST.map((item) => (
              <div key={item.label} className="home-room-list__item">
                <b className="home-room-list__count">{item.count}</b>
                <span className="home-room-list__label">{item.label}</span>
              </div>
            ))}
          </div>

          <p className="home-room-rules">{STAY_RULES}</p>
        </div>
      </section>

      {/* CENNÍK */}
      <section id="cennik" className="home__section home__section--flush-top">
        <div className="home__wrap">
          <div className="home__head">
            <div className="home__eyebrow">Cenník</div>
            <h2 className="home__title">Ceny za osobu a noc</h2>
            <p className="home__lead">
              Ráno je káva, čaj a pečivo zdarma. Skupinám od 12 osôb pošleme
              ponuku na mieru.
            </p>
          </div>

          <div className="home-prices">
            {PRICES.map((item) => (
              <div key={item.label} className="home-prices__item">
                <b className="home-prices__value">{item.value}</b>
                <span className="home-prices__label">{item.label}</span>
              </div>
            ))}
          </div>

          <ul className="home-price-terms">
            {PRICE_TERMS.map((term) => (
              <li key={term}>{term}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* AREÁL */}
      <section id="areal" className="home__section home__section--white">
        <div className="home__wrap">
          <div className="home__head">
            <div className="home__eyebrow">Areál</div>
            <h2 className="home__title">Čo máte k dispozícii, keď ste u nás</h2>
          </div>

          <div className="home-amen">
            {AMENITIES.map((item) => (
              <div key={item.label} className="home-amen__item">
                <svg
                  className="home-amen__icon"
                  viewBox="0 0 40 40"
                  aria-hidden="true"
                >
                  {item.icon}
                </svg>
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OKOLIE */}
      <section id="okolie" className="home__section">
        <div className="home__wrap">
          <div className="home__head">
            <div className="home__eyebrow">Okolie</div>
            <h2 className="home__title">
              Kam sa dá ísť, keď sa dojedia raňajky
            </h2>
            <p className="home__lead">Odporúčame len to, kam chodíme sami.</p>
          </div>

          <div className="home-around">
            {AROUND.map((place) => (
              <div key={place.title} className="home-around__item">
                <img
                  className="home-around__img"
                  src={place.image}
                  alt={place.imageAlt}
                  loading="lazy"
                />
                <div className="home-around__distance">{place.distance}</div>
                <h4 className="home-around__title">{place.title}</h4>
                <p className="home-around__text">{place.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRÍBEH MIESTA */}
      <section className="home__section home-story">
        <div className="home__wrap home-story__wrap">
          <div className="home-story__prose">
            <div className="home__eyebrow home__eyebrow--on-dark">
              Príbeh miesta
            </div>
            <h2 className="home__title home-story__title">
              Chata Limba, Penzión Bystrina, Triferis
            </h2>
            <p className="home-story__text">
              Všetko sa začalo pánom Petrúchom. Postavil tu chatu, otvoril ju
              hosťom a zostal pri nej celý život. Pod menom Penzión Bystrina
              pribudli izby, kuchyňa, ihriská aj chatky – z chaty sa stal areál,
              ktorý uživí celú školu naraz.
            </p>
            <p className="home-story__text">
              A potom sa areál dostal k nám a s ním aj nové meno. Triferis sú
              tri zvieratá tejto doliny – sokol, medveď a rys. Tri úplne odlišné
              povahy, ktoré tu žijú vedľa seba. Tak ako naši hostia.
            </p>
            <div className="home-story__sig">bratia Makvovci</div>
          </div>
          <div className="home-story__logo">
            <img
              src={`${IMG}/triferis-emblem-ring-on-dark.svg`}
              alt="Pečať Penziónu Triferis – sokol, medveď a rys"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* KONTAKT A DOPYT */}
      <section id="kontakt" className="home__section">
        <div className="home__wrap">
          <div className="home__head">
            <div className="home__eyebrow">Kontakt a dopyt</div>
            <h2 className="home__title">
              Ozveme sa do 24 hodín. Väčšinou skôr.
            </h2>
          </div>

          <div className="home-contact">
            <div className="home-contact__info">
              <div>
                <a className="home-contact__value" href="tel:+421 944 526 240">
                  +421 944 526 240
                </a>
                <span className="home-contact__hint">denne 8:00 – 20:00</span>
              </div>
              <div>
                <a
                  className="home-contact__value"
                  href="mailto:info@triferis.sk"
                >
                  info@triferis.sk
                </a>
                <span className="home-contact__hint">
                  skupiny, ponuky, faktúry
                </span>
              </div>
              <div>
                <b className="home-contact__value">Bystrá 118, 977 01 Bystrá</b>
                <span className="home-contact__hint">
                  okres Brezno ·{" "}
                  <a href={MAPS_URL} target="_blank" rel="noopener noreferrer">
                    otvoriť v mapách
                  </a>
                </span>
              </div>
              <div>
                <span className="home-contact__hint">
                  Prevádzkovateľ: Táborčatá s. r. o., Budatínska 3230/16A, 851
                  06 Bratislava · IČO 56040890 · taborcata.sk
                </span>
              </div>
              <iframe
                className="home-contact__map"
                title="Mapa – Penzión Triferis, Bystrá 118"
                src={MAP_EMBED_URL}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>

            <form className="home-form" onSubmit={handleInquirySubmit}>
              <div className="home-form__row">
                <label className="home-form__field">
                  Meno a organizácia
                  <input
                    className="home-form__input"
                    type="text"
                    placeholder="Jana Nováková, ZŠ Brezno"
                    value={inquiry.name}
                    onChange={(e) =>
                      handleInquiryChange("name", e.target.value)
                    }
                    required
                  />
                </label>
                <label className="home-form__field">
                  E-mail alebo telefón
                  <input
                    className="home-form__input"
                    type="text"
                    placeholder="jana@zsbrezno.sk"
                    value={inquiry.contact}
                    onChange={(e) =>
                      handleInquiryChange("contact", e.target.value)
                    }
                    required
                  />
                </label>
              </div>

              <div className="home-form__row">
                <label className="home-form__field">
                  Typ pobytu
                  <select
                    className="home-form__input"
                    value={inquiry.stayType}
                    onChange={(e) =>
                      handleInquiryChange("stayType", e.target.value)
                    }
                  >
                    {STAY_TYPES.map((type) => (
                      <option key={type}>{type}</option>
                    ))}
                  </select>
                </label>
                <label className="home-form__field">
                  Počet osôb
                  <input
                    className="home-form__input"
                    type="number"
                    min="1"
                    placeholder="42"
                    value={inquiry.guests}
                    onChange={(e) =>
                      handleInquiryChange("guests", e.target.value)
                    }
                  />
                </label>
              </div>

              <div className="home-form__row">
                <label className="home-form__field">
                  Príchod
                  <input
                    className="home-form__input"
                    type="date"
                    value={inquiry.arrival}
                    onChange={(e) =>
                      handleInquiryChange("arrival", e.target.value)
                    }
                  />
                </label>
                <label className="home-form__field">
                  Odchod
                  <input
                    className="home-form__input"
                    type="date"
                    min={inquiry.arrival || undefined}
                    value={inquiry.departure}
                    onChange={(e) =>
                      handleInquiryChange("departure", e.target.value)
                    }
                  />
                </label>
              </div>

              <label className="home-form__field">
                Čo by ste ešte potrebovali
                <textarea
                  className="home-form__input"
                  rows="3"
                  placeholder="Obed na svahu, zdravotník, bezlepková strava…"
                  value={inquiry.note}
                  onChange={(e) => handleInquiryChange("note", e.target.value)}
                />
              </label>

              <CaptchaWidget ref={captchaRef} onVerify={setCaptchaToken} />

              <div>
                <button
                  className="home__btn"
                  type="submit"
                  disabled={inquiryStatus === "sending"}
                >
                  {inquiryStatus === "sending" ? "Odosielam…" : "Poslať dopyt"}
                </button>
              </div>

              <div
                className={`home-form__note${inquiryStatus === "error" ? " home-form__note--error" : ""}`}
                role="status"
              >
                {inquiryNote}
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
